
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus } from "lucide-react";

interface EmailTemplate {
    id?: number;
    name: string;
    subject: string;
    body: string;
    category?: string;
}

interface EmailTemplateEditorProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    template?: EmailTemplate | null;
    onSave: (template: EmailTemplate) => Promise<void>;
}

const VARIABLES = [
    { label: "Prénom", value: "{{prenom}}" },
    { label: "Nom", value: "{{nom}}" },
    { label: "Nom Classe", value: "{{nom_classe}}" },
    { label: "URL Accès", value: "{{url_acces}}" },
    { label: "Heure Début", value: "{{heure_debut}}" },
];

const EmailTemplateEditor: React.FC<EmailTemplateEditorProps> = ({
    isOpen,
    onOpenChange,
    template,
    onSave
}) => {
    const [name, setName] = useState("");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [category, setCategory] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    // Cursor tracking
    const subjectRef = React.useRef<HTMLInputElement>(null);
    const bodyRef = React.useRef<HTMLTextAreaElement>(null);
    const [lastFocused, setLastFocused] = useState<'subject' | 'body' | null>(null);

    useEffect(() => {
        if (template) {
            setName(template.name);
            setSubject(template.subject);
            setBody(template.body);
            setCategory(template.category || "");
        } else {
            setName("");
            setSubject("");
            setBody("");
            setCategory("");
        }
    }, [template, isOpen]);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name || !subject || !body) return;

        setIsSaving(true);
        try {
            await onSave({
                id: template?.id,
                name,
                subject,
                body,
                category
            });
            onOpenChange(false);
        } finally {
            setIsSaving(false);
        }
    };

    const insertVariable = (variable: string) => {
        if (lastFocused === 'subject' && subjectRef.current) {
            const input = subjectRef.current;
            const start = input.selectionStart || 0;
            const end = input.selectionEnd || 0;
            const newSubject = subject.substring(0, start) + variable + subject.substring(end);

            setSubject(newSubject);
            setTimeout(() => {
                input.focus();
                input.setSelectionRange(start + variable.length, start + variable.length);
            }, 0);
        } else if (lastFocused === 'body' && bodyRef.current) {
            const textarea = bodyRef.current;
            const start = textarea.selectionStart || 0;
            const end = textarea.selectionEnd || 0;
            const newBody = body.substring(0, start) + variable + body.substring(end);

            setBody(newBody);
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + variable.length, start + variable.length);
            }, 0);
        } else {
            // Fallback
            setBody(prev => prev + variable);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px]">
                <DialogHeader>
                    <DialogTitle>{template ? "Modifier le modèle" : "Créer un modèle"}</DialogTitle>
                    <DialogDescription>
                        Configurez le contenu de votre email. Utilisez les variables pour personnaliser le message.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSave} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Nom du modèle</Label>
                            <Input
                                id="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Ex: Invitation Standard"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="category">Catégorie (Optionnel)</Label>
                            <Input
                                id="category"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                placeholder="Ex: VirtualClass"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="subject">Sujet de l'email</Label>
                        <Input
                            id="subject"
                            ref={subjectRef}
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            onFocus={() => setLastFocused('subject')}
                            placeholder="Ex: Votre invitation pour {{nom_classe}}"
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <Label className="flex justify-between items-center">
                            Corps du message
                            <span className="text-xs text-muted-foreground">HTML supporté</span>
                        </Label>
                        <div className="flex flex-wrap gap-2 mb-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
                            <span className="text-xs font-bold text-slate-500 mr-1 self-center">Insérer :</span>
                            {VARIABLES.map(v => (
                                <button
                                    key={v.value}
                                    type="button"
                                    onClick={() => insertVariable(v.value)}
                                    className="text-[10px] bg-white border border-slate-200 px-2 py-1 rounded-md hover:border-primary hover:text-primary transition-colors shadow-sm font-medium"
                                >
                                    {v.label}
                                </button>
                            ))}
                        </div>
                        <Textarea
                            ref={bodyRef}
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            onFocus={() => setLastFocused('body')}
                            placeholder="Bonjour {{prenom}}, ..."
                            className="min-h-[200px] font-mono text-sm"
                            required
                        />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Annuler
                        </Button>
                        <Button type="submit" disabled={isSaving}>
                            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {template ? "Mettre à jour" : "Créer le modèle"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default EmailTemplateEditor;
